import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { RoomDocument } from '../api/types';
import { formatDate, formatDateOnly } from '../lib/date-only';
import {
  DOCUMENT_SORT_KEYS,
  nextDocumentSort,
  type DocumentSort,
  type DocumentSortKey,
} from '../lib/document-sort';
import { STATEMENT_BASIS_LABELS, statementBasisText } from '../lib/statement-basis';
import { cn } from '../lib/utils';
import { Icon } from './icons';
import { RowMenu, type RowMenuItem } from './row-menu';

/**
 * The data room's document list, shared by the admin's Data room and the
 * seller's "Your documents" (owner, 2026-10-15: "The seller's document area
 * should look just like the admin's"): the table (≥768px) / cards (<768px) with
 * sortable columns, each row's filing chips and file notes, and the facet chips
 * above it. Each page supplies its own rows — the status it shows and the
 * actions it allows — so nothing admin-only reaches the seller.
 */

/** Who is looking: the wording of a few notes differs (the seller is the one asked). */
export type DataRoomAudience = 'admin' | 'seller';

/** The ✕ inside an applied filter chip: pressing the chip again removes it. */
export function ActiveChipClose() {
  return (
    <>
      <Icon name="close" size={12} className="-mr-1 ml-0.5" />
      <span className="sr-only"> (applied — press to remove)</span>
    </>
  );
}

const FACET_CHIP =
  'inline-flex h-8 items-center gap-1.5 rounded-pill border px-3 text-[13px] font-medium transition-colors';

/** "Couldn't read (N)": files we could not read, which the seller has been asked to send again (ADR-0051). */
export function UnreadableFilterChip({
  count,
  active,
  onToggle,
  title,
}: {
  count: number;
  active: boolean;
  onToggle: () => void;
  title: string;
}) {
  if (count === 0 && !active) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <button
        type="button"
        aria-pressed={active}
        onClick={onToggle}
        title={title}
        className={cn(
          FACET_CHIP,
          active
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-st-check-line bg-st-check text-st-check-fg hover:opacity-90',
        )}
      >
        <Icon name="alert" size={14} />
        Couldn’t read ({count}){active ? <ActiveChipClose /> : null}
      </button>
    </div>
  );
}

/** A document's tags under its name: the filing path (last three folders, the
 * deepest in bold; the full path in the tooltip), then its period and the date
 * of data (the folder tree groups by the year). `withDataDate={false}` where a
 * column already shows the date. */
export function DocumentTagChips({
  document,
  withDataDate = true,
}: {
  document: Pick<RoomDocument, 'categoryPath' | 'period' | 'dataDate' | 'statementBasis'>;
  withDataDate?: boolean;
}) {
  const basis = document.statementBasis ?? null;
  const dataDate = withDataDate ? (document.dataDate ?? null) : null;
  if (document.categoryPath.length === 0 && !document.period && !dataDate && !basis) {
    return null;
  }
  const path = document.categoryPath;
  const shown = path.slice(-3);
  const clipped = path.length > shown.length;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-ink-2">
      {path.length > 0 ? (
        <span className="inline-flex flex-wrap items-center gap-x-1" title={path.join(' › ')}>
          <span className="sr-only">{path.join(' / ')}</span>
          <span aria-hidden="true" className="inline-flex flex-wrap items-center gap-x-1">
            {clipped ? <span>…</span> : null}
            {shown.map((seg, i) => (
              <span key={`${seg}-${i}`} className="inline-flex items-center gap-x-1">
                {i > 0 || clipped ? (
                  <Icon name="chevronRight" size={12} className="text-ink-3" />
                ) : null}
                <span className={i === shown.length - 1 ? 'font-semibold text-ink' : undefined}>
                  {seg}
                </span>
              </span>
            ))}
          </span>
        </span>
      ) : null}
      {document.period ? (
        <span className="rounded-sm border bg-surface-2 px-1.5 font-medium text-ink-2">
          {document.period}
        </span>
      ) : null}
      {dataDate ? (
        <span
          className="rounded-sm border bg-surface-2 px-1.5 font-medium tabular-nums text-ink-2"
          title="Date of data"
        >
          {formatDateOnly(dataDate)}
        </span>
      ) : null}
      {basis ? (
        <span
          className="rounded-sm border bg-surface-2 px-1.5 font-medium text-ink-2"
          title={`Accounting basis: ${statementBasisText(basis)}`}
        >
          {basis.basis === 'not_stated'
            ? 'Basis not stated'
            : `${STATEMENT_BASIS_LABELS[basis.basis]} basis`}
        </span>
      ) : null}
    </div>
  );
}

/**
 * What the data room says about a document's FILE, under its name (ADR-0018
 * update 2026-10-09): that we could not read it, that it is a zip or saved
 * email whose files were filed one by one, or which one it came out of. The
 * admin is told the seller has been asked for a readable copy; the seller is
 * shown the request itself. The trial limit is the admin's plan alone.
 * `unreadableOptions`: what the reader can do about a file we couldn't read
 * (`UnreadableFileOptions` — open, download, replace, clear), under the note.
 */
export function DocumentFileNotes({
  document,
  audience = 'admin',
  unreadableOptions,
}: {
  document: Pick<RoomDocument, 'unreadable' | 'archive' | 'fromArchive'>;
  audience?: DataRoomAudience;
  unreadableOptions?: ReactNode;
}) {
  const { unreadable, archive, fromArchive } = document;
  if (!unreadable && !archive && !fromArchive) return null;
  return (
    <div className="mt-1 space-y-0.5 text-xs">
      {unreadable ? (
        <p className="flex items-start gap-1 text-st-check-fg">
          <Icon name="alert" size={12} className="mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">Couldn’t read: </span>
            {audience === 'seller' ? (
              unreadable.message
            ) : (
              <>
                {unreadable.message.replace(/ — please upload.*$/, '.')} The seller has been asked
                for a readable copy.
              </>
            )}
          </span>
        </p>
      ) : null}
      {unreadable ? unreadableOptions : null}
      {archive ? (
        <p className="text-ink-2">
          Unpacked: {archive.files === 1 ? '1 file' : `${archive.files} files`} filed on their own
        </p>
      ) : null}
      {audience === 'admin' && archive?.overTrialLimit ? (
        <p className="text-st-check-fg">
          Partly unpacked —{' '}
          {archive.overTrialLimit === 1 ? '1 file' : `${archive.overTrialLimit} files`} over the
          trial limit. They are added when you upgrade.
        </p>
      ) : null}
      {fromArchive ? (
        <p className="break-words text-ink-3">From {fromArchive.name ?? 'a zip or email'}</p>
      ) : null}
    </div>
  );
}

/** One row of the list, as the page decides it. */
export interface DocumentListRow {
  /** The document as found (a listing row, or a search hit). */
  doc: RoomDocument;
  /** The polled listing's copy (search hits carry no tags or notes). */
  live: RoomDocument;
  /** The Status cell — `table` stacks it, `card` runs it inline. */
  status: (layout: 'table' | 'card') => ReactNode;
  /** The ⋯ menu's actions (none → no menu). */
  actions: RowMenuItem[];
  /** "Change folder or date": opens the Manage document dialog from the row itself
   * (none → no such link, as for buyers). */
  onManage?: () => void;
  /** What clicking the name does (none → the name is plain text). */
  onOpen?: () => void;
  /** The name button's tooltip ("View file", "Review classification"). */
  openTitle?: string;
  /** Under a "Couldn't read" note: what the reader can do about it (`UnreadableFileOptions`). */
  unreadableOptions?: ReactNode;
}

/** The document's name: opens whatever the page decided (the viewer, a review). */
function DocName({ row }: { row: DocumentListRow }) {
  const { doc, onOpen, openTitle } = row;
  if (!onOpen) return <span className="break-words font-semibold text-ink-2">{doc.name}</span>;
  return (
    <button
      type="button"
      title={openTitle}
      className="break-words text-left font-semibold text-ink hover:underline"
      onClick={onOpen}
    >
      {doc.name}
    </button>
  );
}

/** The row's own way into Manage document — visible, so moving a document or fixing its date is not hidden in a viewer or a menu. */
function ManageLink({ row, aside }: { row: DocumentListRow; aside?: ReactNode }) {
  if (!row.onManage) return null;
  const link = (
    <button
      type="button"
      className="block whitespace-normal text-left text-xs font-medium text-primary hover:underline"
      onClick={row.onManage}
    >
      Change folder or date
      <span className="sr-only"> of {row.doc.name}</span>
    </button>
  );
  if (!aside) return link;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {link}
      {aside}
    </div>
  );
}

const SORT_LABELS: Record<DocumentSortKey, string> = {
  name: 'Document',
  dataDate: 'Date of data',
  uploaded: 'Added',
};

/** The narrow-screen sort menu's choices: each column, both ways. */
const SORT_CHOICES: { sort: DocumentSort; label: string }[] = DOCUMENT_SORT_KEYS.flatMap(
  (key): { sort: DocumentSort; label: string }[] =>
    key === 'name'
      ? [
          { sort: { key, dir: 'asc' as const }, label: 'Name, A to Z' },
          { sort: { key, dir: 'desc' as const }, label: 'Name, Z to A' },
        ]
      : [
          { sort: { key, dir: 'desc' as const }, label: `${SORT_LABELS[key]}, newest first` },
          { sort: { key, dir: 'asc' as const }, label: `${SORT_LABELS[key]}, oldest first` },
        ],
);
const sortValue = (s: DocumentSort) => `${s.key}:${s.dir}`;
const sortChoiceLabel = (s: DocumentSort) =>
  SORT_CHOICES.find((c) => sortValue(c.sort) === sortValue(s))?.label ?? '';

/** A sortable column header: the whole label is the button; the arrow shows the direction. */
function SortHeader({
  column,
  sort,
  onSort,
  className,
}: {
  column: DocumentSortKey;
  sort: DocumentSort | null;
  onSort: (sort: DocumentSort) => void;
  className?: string;
}) {
  const current = sort && sort.key === column ? sort : null;
  const active = current !== null;
  const asc = current?.dir === 'asc';
  return (
    <th
      scope="col"
      aria-sort={active ? (asc ? 'ascending' : 'descending') : 'none'}
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(nextDocumentSort(sort, column))}
        title={
          current
            ? `${sortChoiceLabel(current)} — press to reverse`
            : `Sort by ${SORT_LABELS[column].toLowerCase()}`
        }
        className={cn(
          '-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active && 'text-ink',
        )}
      >
        {SORT_LABELS[column]}
        <Icon
          name="chevronDown"
          size={12}
          className={cn(active ? 'opacity-100' : 'opacity-0', asc && 'rotate-180')}
        />
      </button>
    </th>
  );
}

/**
 * The list itself. ≥768px: a table — Document (who added it folds under the
 * name) · Date of data · Added · Status · ⋯ — whose Document, Date of data and Added headers
 * sort (press again to reverse; `aria-sort` on the header). <768px: the same
 * rows as cards, with a "Sort by" menu in place of the headers. `sort` null =
 * the order given (a search's best match first).
 */
export function DocumentList({
  rows,
  sort,
  onSort,
  audience = 'admin',
  manageAside,
}: {
  rows: DocumentListRow[];
  sort: DocumentSort | null;
  onSort: (sort: DocumentSort) => void;
  audience?: DataRoomAudience;
  /** A "?" help tip shown once, after the first row's "Change folder or date" link. */
  manageAside?: ReactNode;
}) {
  const selectId = useId();
  return (
    <>
      <div className="hidden md:block">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b text-left text-xs font-semibold text-ink-2">
              <SortHeader column="name" sort={sort} onSort={onSort} className="py-2.5 pr-4" />
              <SortHeader
                column="dataDate"
                sort={sort}
                onSort={onSort}
                className="w-[116px] py-2.5 pr-4"
              />
              <SortHeader
                column="uploaded"
                sort={sort}
                onSort={onSort}
                className="w-[116px] py-2.5 pr-4"
              />
              <th scope="col" className="w-[150px] py-2.5 pr-2">
                Status
              </th>
              <th scope="col" className="w-12 py-2.5">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.doc.id} className="border-b align-top last:border-0 hover:bg-surface-2/60">
                <td className="py-3 pr-4">
                  <DocName row={r} />
                  <DocumentTagChips document={r.live} withDataDate={false} />
                  <DocumentFileNotes
                    document={r.live}
                    audience={audience}
                    unreadableOptions={r.unreadableOptions}
                  />
                  <span className="mt-1 block truncate text-xs text-ink-3">
                    Added by {r.doc.uploadedBy ?? 'unknown'}
                  </span>
                </td>
                <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                  {r.live.dataDate ? (
                    formatDateOnly(r.live.dataDate)
                  ) : (
                    <span className="text-ink-3" title="No date of data">
                      —
                    </span>
                  )}
                  <ManageLink row={r} aside={i === 0 ? manageAside : undefined} />
                </td>
                <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                  {formatDate(r.doc.createdAt)}
                </td>
                <td className="py-3 pr-2">{r.status('table')}</td>
                <td className="py-2 text-right">
                  {r.actions.length > 0 ? <RowMenu label={r.doc.name} items={r.actions} /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* <768px: the same rows as cards, so nothing scrolls sideways. */}
      <div className="mb-2 flex items-center gap-2 md:hidden">
        <label htmlFor={selectId} className="shrink-0 text-xs font-medium text-ink-2">
          Sort by
        </label>
        <select
          id={selectId}
          value={sort ? sortValue(sort) : ''}
          onChange={(e) => {
            const choice = SORT_CHOICES.find((c) => sortValue(c.sort) === e.target.value);
            if (choice) onSort(choice.sort);
          }}
          className="min-h-tap min-w-0 flex-1 rounded-md border border-input bg-surface px-2 text-sm"
        >
          {sort ? null : <option value="">Best match</option>}
          {SORT_CHOICES.map((c) => (
            <option key={sortValue(c.sort)} value={sortValue(c.sort)}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      <ul className="flex flex-col gap-2 md:hidden" aria-label="Documents">
        {rows.map((r, i) => (
          <li
            key={r.doc.id}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-lg border bg-surface p-3"
          >
            <div className="min-w-0">
              <DocName row={r} />
              <DocumentTagChips document={r.live} />
              <DocumentFileNotes
                document={r.live}
                audience={audience}
                unreadableOptions={r.unreadableOptions}
              />
              <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-xs text-ink-3">
                {r.status('card')}
                <span className="min-w-0 truncate">
                  {r.doc.uploadedBy ? `${r.doc.uploadedBy} · ` : ''}
                  {formatDate(r.doc.createdAt)}
                </span>
              </div>
              <div className="mt-1.5">
                <ManageLink row={r} aside={i === 0 ? manageAside : undefined} />
              </div>
            </div>
            {r.actions.length > 0 ? <RowMenu label={r.doc.name} items={r.actions} /> : null}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The search box's text lives in the URL, but the box owns its keystrokes
 * (debounced). When the URL's text changes from outside it — Back, a chip's ✕,
 * "Show all documents" — the box is remounted (a new `key`) so it shows the
 * URL's text. `onQueryChange` writes the box's text back: starting a search is
 * a step Back undoes (`replace` false); refining or clearing it is not.
 */
export function useDataRoomSearchBox(
  query: string,
  setQuery: (query: string, replace: boolean) => void,
): { key: number; onQueryChange: (q: string) => void } {
  const reported = useRef(query);
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (query !== reported.current) {
      reported.current = query;
      setKey((k) => k + 1);
    }
  }, [query]);
  const onQueryChange = (q: string) => {
    if (q === reported.current) return;
    const replace = reported.current !== '';
    reported.current = q;
    setQuery(q, replace);
  };
  return { key, onQueryChange };
}
